"use client";

import { cn } from "@/lib/utils";
import {
  ctaClick,
  carouselClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { hexToRgba } from "@/utils/utils";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// ── Icon Components ──────────────────────────────────────────────────────────

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";

import { useDispatch } from "react-redux";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

// Derives a soft drop-shadow colour from the CTA button's own background colour
// (hex or rgb/rgba) at the given opacity — so the shadow always matches the button.
const shadowFromColor = (color: string, alpha = 0.6): string => {
  if (!color) return "transparent";
  const c = color.trim();
  if (c.startsWith("#")) {
    let hex = c.slice(1);
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((ch) => ch + ch)
        .join("");
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return c;
  }
  const m = c.match(/^rgba?\(([^)]+)\)/i);
  if (m) {
    const [r, g, b] = m[1].split(",").map((p) => p.trim());
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return c;
};

const ArrowLeftIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15 19.92L8.48 13.4C7.71 12.63 7.71 11.37 8.48 10.6L15 4.08"
      stroke="white"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.91 19.92L15.43 13.4C16.2 12.63 16.2 11.37 15.43 10.6L8.91 4.08"
      stroke="white"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowDownIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 5V19M12 19L6 13M12 19L18 13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Figma "Led by Visionaries" (variant_2) icons. Stroke follows `currentColor` so the
// caller sets the colour — black on the light v2 background, where the white v1
// chevrons/arrows are invisible.
const ArrowLeftLineIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.57 5.93L3.5 12L9.57 18.07"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20.5 12H3.67"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightLineIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.43 5.93L20.5 12L14.43 18.07"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 12H20.33"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// vuesax/linear/export — the CTA icon in the variant_2 design.
const ExportIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13 11L21.2 2.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 6.83V2H17.17"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Types ────────────────────────────────────────────────────────────────────

interface Faculty2MetaItem {
  id: string;
  title_1: string;
  title_1_color: string;
  /** Mobile override; falls back to `title_1_color` when unset. */
  mobile_title_1_color?: string;
  title_2: string;
  title_2_color: string;
  horizontal_stripe_color: string;
  designation: string;
  designation_color: string;
  /** Mobile override; falls back to `designation_color` when unset. */
  mobile_designation_color?: string;
  logo_1: string;
  logo_1_alt: string;
  logo_2: string;
  logo_2_alt: string;
  description_title: string;
  description_title_color: string;
  description: string;
  description_color: string;
  desktop_bg_image: string;
  mobile_bg_image: string;
  alt_image: string;
  description_background_color: string;
  priority: number;
  status: boolean;
}

interface Faculty2Data {
  status: boolean;
  title_1: string;
  title_1_color: string;
  title_2: string;
  title_2_gradient_start_color: string;
  title_2_gradient_end_color: string;
  title_2_gradient_angle: string;
  left_navigation_icon: string;
  left_navigation_icon_alt: string;
  right_navigation_icon: string;
  right_navigation_icon_alt: string;
  range_color: string;
  cta_text: string;
  cta_button_color: string;
  cta_text_color: string;
  cta_icon: string;
  cta_icon_alt: string;
  auto_scroll_frequency: number;
  backvariant: string;
  layout_variant: string;
  gradient_color_1: string;
  gradient_color_2: string;
  gradient_color_3: string;
  gradient_color_4: string;
  background_color: string;
  metadata: Faculty2MetaItem[];
}

// ── Mobile Faculty Card ──────────────────────────────────────────────────────

const MobileFacultyCard = ({ item }: { item: Faculty2MetaItem }) => {
  const {
    title_1 = "",
    title_1_color = "#FFFFFF",
    mobile_title_1_color = "",
    title_2 = "",
    title_2_color = "#00D4EB",
    horizontal_stripe_color = "#FFFFFF",
    designation = "",
    designation_color = "rgba(255,255,255,0.8)",
    mobile_designation_color = "",
    logo_1 = "",
    logo_1_alt = "Logo",
    logo_2 = "",
    logo_2_alt = "Logo",
    description_title = "",
    description_title_color = "#4CECFA",
    description: desc = "",
    description_color = "rgba(255,255,255,0.8)",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "Faculty",
    description_background_color = "#242424",
  } = item;

  // Mobile-only colour overrides (layout variant 2 needs a different title /
  // designation colour on mobile). Blank means "inherit the web colour".
  const titleColor = mobile_title_1_color || title_1_color;
  const designationColor = mobile_designation_color || designation_color;

  return (
    <div className="flex flex-col w-full">
      {/* Image Section */}
      <div className="relative w-full h-[230px] rounded-t-[20px] overflow-hidden bg-gradient-to-b from-[#342b7c] to-[#02a7b6]">
        {(mobile_bg_image || desktop_bg_image) && (
          <Image
            src={mobile_bg_image || desktop_bg_image}
            alt={alt_image}
            fill
            className="object-cover object-top"
            sizes="100vw"
          />
        )}

        <div className="absolute bottom-0 left-0 w-full h-[72px] bg-gradient-to-t from-black/80 to-transparent z-[1]" />

        <div className="absolute bottom-[16px] left-[16px] right-[16px] flex items-end justify-between z-10">
          {designation && (
            <p
              className="text-[14px] font-semibold leading-[1.49] max-w-[196px]"
              style={{ color: designationColor }}
            >
              {designation}
            </p>
          )}
          {(logo_1 || logo_2) && (
            <div className="flex items-start gap-[6px] shrink-0">
              {logo_1 && (
                <div className="relative w-[75px] h-[30px] rounded-[3px] overflow-hidden">
                  <Image
                    src={logo_1}
                    alt={logo_1_alt}
                    fill
                    className="object-contain"
                    sizes="75px"
                  />
                </div>
              )}
              {logo_2 && (
                <div className="relative w-[42px] h-[30px] rounded-[3px] overflow-hidden">
                  <Image
                    src={logo_2}
                    alt={logo_2_alt}
                    fill
                    className="object-contain"
                    sizes="42px"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Description Section */}
      <div
        className="w-full rounded-b-[20px] p-[14px] flex flex-col gap-[24px]"
        style={{ backgroundColor: description_background_color }}
      >
        {(title_1 || title_2) && (
          <div className="flex flex-col items-start gap-[6px] w-[219px] leading-[1.4]">
            {title_1 && (
              <p
                className="font-denton-test font-bold text-[18px] tracking-[0.18px]"
                style={{ color: titleColor }}
              >
                {title_1}
              </p>
            )}
            <div
              className="w-[14px] h-[1px]"
              style={{ backgroundColor: horizontal_stripe_color }}
            />
            {title_2 && (
              <p
                className="font-denton-test italic font-medium text-[14px] tracking-[0.14px]"
                style={{ color: title_2_color }}
              >
                {title_2}
              </p>
            )}
          </div>
        )}

        <div className="text-[14px] font-semibold leading-[1.49]">
          {description_title && (
            <p style={{ color: description_title_color }}>
              {description_title}
            </p>
          )}
          {desc && <p style={{ color: description_color }}>{desc}</p>}
        </div>
      </div>
    </div>
  );
};

// ── Desktop Faculty Card ─────────────────────────────────────────────────────

const FacultyCard = ({
  item,
  isV2 = false,
}: {
  item: Faculty2MetaItem;
  isV2?: boolean;
}) => {
  const {
    title_1 = "",
    title_1_color = "#FFFFFF",
    title_2 = "",
    title_2_color = "#00D4EB",
    horizontal_stripe_color = "#FFFFFF",
    designation = "",
    designation_color = "rgba(255,255,255,0.8)",
    logo_1 = "",
    logo_1_alt = "Logo",
    logo_2 = "",
    logo_2_alt = "Logo",
    description_title = "",
    description_title_color = "#4CECFA",
    description: desc = "",
    description_color = "rgba(255,255,255,0.9)",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "Faculty",
    description_background_color = "#242424",
  } = item;

  return (
    <div
      className={cn(
        "flex overflow-hidden",
        "xs:flex-col xs:rounded-[16px]",
        "sm:flex-row sm:items-center sm:rounded-[20px] sm:h-[340px]",
        "md:h-[400px]",
        "lg:h-[467px]",
        "xl:h-[467px]",
      )}
      style={{
        background: "linear-gradient(to bottom, #342b7c, #02a7b6)",
      }}
    >
      {/* Image Panel */}
      <div
        className={cn(
          "relative shrink-0",
          "xs:w-full xs:h-[240px]",
          "sm:w-[233px] sm:h-full",
          "md:w-[274px]",
          "lg:w-[320px]",
          "xl:w-[320px]",
        )}
      >
        {/* Gradient background base */}
        <div className="absolute inset-0 rounded-l-[20px] bg-gradient-to-b from-[#342b7c] to-[#02a7b6]" />

        {/* Mobile image */}
        {(mobile_bg_image || desktop_bg_image) && (
          <div className="absolute inset-0 overflow-hidden xs:block sm:hidden">
            <Image
              src={mobile_bg_image || desktop_bg_image}
              alt={alt_image}
              fill
              className="object-cover object-top"
              sizes="100vw"
            />
          </div>
        )}

        {/* Desktop image */}
        {desktop_bg_image && (
          <div className="absolute inset-0 overflow-hidden xs:hidden sm:block sm:rounded-l-[20px]">
            <Image
              src={desktop_bg_image}
              alt={alt_image}
              fill
              className="object-cover object-top"
              sizes="(max-width: 1080px) 233px, (max-width: 1230px) 274px, (max-width: 1430px) 320px, 320px"
            />
          </div>
        )}

        {/* Top Info - Title + Author */}
        <div
          className={cn(
            "absolute left-1/2 -translate-x-1/2 flex flex-col items-center z-10",
            "xs:top-[12px] xs:w-[180px] xs:gap-[4px]",
            "sm:top-[16px] sm:w-[220px] sm:gap-[6px]",
            "md:top-[26px] md:w-[220px] md:gap-[6px]",
            "lg:top-[26px] lg:w-[219px] lg:gap-[6px]",
            "xl:top-[26px] xl:w-[219px] xl:gap-[6px]",
          )}
        >
          {title_1 && (
            <p
              className={cn(
                "w-full text-center leading-140",
                isV2
                  ? // Figma: poster title stays on a single line on web.
                    "font-poppins font-semibold text-[22px] tracking-[-0.11px] whitespace-nowrap"
                  : cn(
                      "font-denton-test font-semibold",
                      "xs:text-[24px] xs:tracking-[0.24px]",
                      "sm:text-[28px] sm:tracking-[0.28px]",
                      "md:text-[30px] md:tracking-[0.3px]",
                      "lg:text-[30px] lg:tracking-[0.3px]",
                      "xl:text-[30px] xl:tracking-[0.3px]",
                    ),
              )}
              style={{ color: title_1_color }}
            >
              {title_1}
            </p>
          )}

          {/* Horizontal stripe */}
          <div
            className={cn(
              "xs:w-[14px] sm:w-[18px] md:w-[18px] lg:w-[18px] xl:w-[18px] h-[1px]",
            )}
            style={{ backgroundColor: horizontal_stripe_color }}
          />

          {title_2 && (
            <p
              className={cn(
                "w-full text-center",
                isV2
                  ? "font-poppins font-normal not-italic text-[15px] leading-[1.55] tracking-[0.005em]"
                  : cn(
                      "font-denton-test italic font-medium leading-140",
                      "xs:text-[12px] xs:tracking-[0.12px]",
                      "sm:text-[14px] sm:tracking-[0.14px]",
                      "md:text-[16px] md:tracking-[0.16px]",
                      "lg:text-[20px] lg:tracking-[0.2px]",
                      "xl:text-[20px] xl:tracking-[0.2px]",
                    ),
              )}
              style={{ color: title_2_color }}
            >
              {title_2}
            </p>
          )}
        </div>

        {/* Bottom gradient fade */}
        {/* <div className="absolute bottom-0 left-0 w-full h-[45%] bg-gradient-to-t from-black/80 to-transparent z-[1]" /> */}

        {/* Bottom Info - Designation + Logos */}
        <div
          className={cn(
            "absolute left-1/2 -translate-x-1/2 flex flex-col items-center z-10",
            "xs:bottom-[12px] xs:gap-[10px]",
            "sm:bottom-[16px] sm:gap-[12px]",
            "md:bottom-[18px] md:gap-[13px]",
            "lg:bottom-[16px] lg:gap-[13px]",
            "xl:bottom-[16px] xl:gap-[13px]",
          )}
        >
          {designation && (
            <p
              className={cn(
                "text-center",
                "xs:w-[160px] sm:w-[179px] md:w-[211px] lg:w-[260px] xl:w-[260px]",
                isV2
                  ? "font-poppins font-normal text-[15px] leading-[1.55] tracking-[0.005em]"
                  : cn(
                      "font-semibold leading-[1.49]",
                      "xs:text-[10px] sm:text-[12px] md:text-[14px] lg:text-[18px] xl:text-[18px]",
                    ),
              )}
              style={{ color: designation_color }}
            >
              {designation}
            </p>
          )}

          {/* Logos */}
          {(logo_1 || logo_2) && (
            <div
              className={cn(
                "flex items-start",
                "xs:gap-[4px] sm:gap-[6px] md:gap-[6px]",
                "lg:gap-[8px] xl:gap-[8px]",
              )}
            >
              {logo_1 && (
                <div
                  className={cn(
                    "relative overflow-hidden",
                    "xs:w-[60px] xs:h-[24px] xs:rounded-[3px]",
                    "sm:w-[75px] sm:h-[30px] sm:rounded-[3px]",
                    "md:w-[75px] md:h-[30px] md:rounded-[3px]",
                    "lg:w-[100px] lg:h-[40px] lg:rounded-[4px]",
                    "xl:w-[100px] xl:h-[40px] xl:rounded-[4px]",
                  )}
                >
                  <Image
                    src={logo_1}
                    alt={logo_1_alt}
                    fill
                    className="object-cover"
                    sizes="100px"
                  />
                </div>
              )}
              {logo_2 && (
                <div
                  className={cn(
                    "relative overflow-hidden",
                    "xs:w-[34px] xs:h-[24px] xs:rounded-[3px]",
                    "sm:w-[42px] sm:h-[30px] sm:rounded-[3px]",
                    "md:w-[42px] md:h-[30px] md:rounded-[3px]",
                    "lg:w-[56px] lg:h-[40px] lg:rounded-[4px]",
                    "xl:w-[56px] xl:h-[40px] xl:rounded-[4px]",
                  )}
                >
                  <Image
                    src={logo_2}
                    alt={logo_2_alt}
                    fill
                    className="object-cover"
                    sizes="58px"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Description */}
      <div
        className={cn(
          "flex flex-col items-start shrink-0 relative z-[1]",
          "xs:w-full xs:p-[16px]",
          // Overlap the photo panel by 24px and sit on top of it so the card's teal
          // gradient can't peek through the panel's rounded bottom-left corner at the
          // seam. Width is bumped by the same 24px so the right edge stays put, and the
          // left padding is bumped by 24px so the text doesn't shift. All four corners
          // stay rounded.
          "sm:w-[278px] sm:-ml-[18px] sm:h-full sm:rounded-[20px] sm:py-[20px] sm:pr-[20px] sm:pl-[44px]",
          "md:w-[298px]",
          "lg:w-[318px]",
          "xl:w-[318px]",
        )}
        style={{ backgroundColor: description_background_color }}
      >
        <div className="flex-1 flex flex-col overflow-hidden">
          {description_title && (
            <p
              className={cn(
                "uppercase",
                isV2
                  ? "font-poppins font-semibold text-[18px] leading-[1.5]"
                  : cn(
                      "font-semibold leading-[1.49]",
                      "xs:text-[10px]",
                      "sm:text-[12px]",
                      "md:text-[14px]",
                      "lg:text-[16px]",
                      "xl:text-[16px]",
                    ),
              )}
              style={{ color: description_title_color }}
            >
              {description_title}
            </p>
          )}
          {desc && (
            <p
              className={cn(
                isV2
                  ? "font-poppins font-normal text-[15px] leading-[1.55] tracking-[0.005em]"
                  : cn(
                      "font-semibold leading-[1.49]",
                      "xs:text-[11px]",
                      "sm:text-[12px]",
                      "md:text-[14px]",
                      "lg:text-[18px]",
                      "xl:text-[18px]",
                    ),
              )}
              style={{ color: description_color }}
            >
              {desc}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const Faculty2 = ({ data }: { data: { data: Faculty2Data } }) => {
  const sectionData = data?.data;
  const ref = useRef<HTMLElement>(null);

  const {
    status,
    title_1 = "Led by Visionaries,",
    title_1_color = "rgba(255, 255, 255, 0.8)",
    title_2 = "Built for You",
    title_2_gradient_start_color = "#FFFFFF",
    title_2_gradient_end_color = "#02A7B6",
    title_2_gradient_angle = "90deg",
    left_navigation_icon = "",
    left_navigation_icon_alt = "Left navigation",
    right_navigation_icon = "",
    right_navigation_icon_alt = "Right navigation",
    range_color = "#FFFFFF",
    cta_text = "",
    cta_button_color = "#FFFFFF",
    cta_text_color = "#222222",
    cta_icon = "",
    cta_icon_alt = "CTA icon",
    auto_scroll_frequency = 5000,
    backvariant = "variant_1",
    layout_variant = "variant_1",
    gradient_color_1 = "#00D4EB",
    gradient_color_2 = "#000000",
    gradient_color_3 = "#000000",
    gradient_color_4 = "#5345C3",
    background_color = "#000000",
    metadata = [],
  } = sectionData || {};

  // Layout variant 2 = Figma "Led by Visionaries" design: Poppins typography +
  // maroon CTA with drop shadow. variant_1 keeps the original styling.
  const isV2 = layout_variant === "variant_2";

  // Filter and sort active metadata
  const activeCards = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: Faculty2MetaItem) => item.status)
      .sort(
        (a: Faculty2MetaItem, b: Faculty2MetaItem) =>
          (a.priority || 0) - (b.priority || 0),
      );
  }, [metadata]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setSnapCount(emblaApi.scrollSnapList().length);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );

  // Mobile carousel
  const [mobileEmblaRef, mobileEmblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const [mobileSelectedIndex, setMobileSelectedIndex] = useState(0);
  const [mobileSnapCount, setMobileSnapCount] = useState(0);

  const onMobileSelect = useCallback(() => {
    if (!mobileEmblaApi) return;
    setMobileSelectedIndex(mobileEmblaApi.selectedScrollSnap());
    setMobileSnapCount(mobileEmblaApi.scrollSnapList().length);
  }, [mobileEmblaApi]);

  useEffect(() => {
    if (!mobileEmblaApi) return;
    onMobileSelect();
    mobileEmblaApi.on("select", onMobileSelect).on("reInit", onMobileSelect);
  }, [mobileEmblaApi, onMobileSelect]);

  const scrollMobilePrev = useCallback(
    () => mobileEmblaApi?.scrollPrev(),
    [mobileEmblaApi],
  );
  const scrollMobileNext = useCallback(
    () => mobileEmblaApi?.scrollNext(),
    [mobileEmblaApi],
  );

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "faculty_section");
  //   }
  // }, []);

  // Track desktop carousel slide viewed
  useEffect(() => {
    if (
      activeCards.length > 0 &&
      selectedIndex >= 0 &&
      selectedIndex < activeCards.length
    ) {
      const item = activeCards[selectedIndex];
      carouselClick({
        section: "faculty_section",
        label:
          item?.title_1 ||
          item?.title_2 ||
          `faculty_slide_${selectedIndex + 1}`,
      });
    }
  }, [selectedIndex, activeCards]);

  // Track mobile carousel slide viewed
  useEffect(() => {
    if (
      activeCards.length > 0 &&
      mobileSelectedIndex >= 0 &&
      mobileSelectedIndex < activeCards.length
    ) {
      const item = activeCards[mobileSelectedIndex];
      carouselClick({
        section: "faculty_section",
        label:
          item?.title_1 ||
          item?.title_2 ||
          `faculty_slide_${mobileSelectedIndex + 1}`,
      });
    }
  }, [mobileSelectedIndex, activeCards]);

  // Auto-scroll for desktop carousel
  useEffect(() => {
    if (!emblaApi || !auto_scroll_frequency || auto_scroll_frequency <= 0)
      return;
    const interval = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    }, auto_scroll_frequency);
    return () => clearInterval(interval);
  }, [emblaApi, auto_scroll_frequency]);

  // Auto-scroll for mobile carousel
  useEffect(() => {
    if (!mobileEmblaApi || !auto_scroll_frequency || auto_scroll_frequency <= 0)
      return;
    const interval = setInterval(() => {
      if (mobileEmblaApi.canScrollNext()) {
        mobileEmblaApi.scrollNext();
      } else {
        mobileEmblaApi.scrollTo(0);
      }
    }, auto_scroll_frequency);
    return () => clearInterval(interval);
  }, [mobileEmblaApi, auto_scroll_frequency]);

  if (!status || activeCards.length === 0) {
    return null;
  }

  // Background gradient helper
  const dispatch = useDispatch();

  const getGradientBackground = () => {
    switch (backvariant) {
      case "variant_1":
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_2":
        return `linear-gradient(292deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
      case "variant_3":
        return background_color;
      default:
        return `linear-gradient(253deg, ${hexToRgba(gradient_color_1, 0.33)} 0%, ${hexToRgba(gradient_color_2, 0.66)} 24.68%, ${hexToRgba(gradient_color_3, 0.66)} 79.09%, ${hexToRgba(gradient_color_4, 0.46)} 100%), ${background_color}`;
    }
  };

  const progressWidth =
    snapCount > 0 ? ((selectedIndex + 1) / snapCount) * 100 : 0;

  const mobileProgressWidth =
    mobileSnapCount > 0
      ? ((mobileSelectedIndex + 1) / mobileSnapCount) * 100
      : 0;

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: background_color }}
    >
      {/* Background gradient overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{ background: getGradientBackground() }}
      />

      {/* Decorative gradient ellipses */}
      <div className="absolute -left-[109px] -top-[87px] w-[461px] h-[461px] rounded-full bg-[rgba(0,212,235,0.12)] blur-[150px]" />
      <div className="absolute right-[-50px] bottom-[-100px] w-[461px] h-[461px] rounded-full bg-[rgba(83,69,195,0.15)] blur-[150px]" />

      {/* Content container */}
      <div
        className={cn(
          "relative z-10 mx-auto flex flex-col",
          "xs:px-[24px] xs:py-[40px] xs:gap-[24px]",
          "sm:max-w-[1080px] sm:py-[60px] sm:px-0 sm:gap-0",
          "md:max-w-[1230px] md:py-[60px]",
          "lg:max-w-[1430px] lg:py-[60px]",
          "xl:max-w-[1750px] xl:py-[80px]",
        )}
      >
        {/* Title Section - left-aligned with section padding */}
        <div
          className={cn(
            "flex flex-col xs:gap-[4px] sm:gap-0",
            // Figma variant_2: section title is centered on both mobile and web.
            isV2 && "items-center text-center",
            "sm:px-[60px]",
            "md:px-[60px]",
            "lg:px-[80px]",
            "xl:px-[140px]",
          )}
        >
          {title_1 && (
            <p
              className={cn(
                isV2
                  ? // Figma: section title is exactly 28px on mobile, 36px on desktop.
                    "font-poppins font-semibold leading-[1.25] tracking-[-0.015em] text-[28px] sm:text-[36px]"
                  : cn(
                      "white-title-wf font-semibold",
                      "xs:text-[24px]",
                      "sm:text-[28px]",
                      "md:text-[30px] lg:text-[30px] xl:text-[30px]",
                    ),
              )}
              style={{ color: title_1_color }}
            >
              {title_1}
            </p>
          )}
          {title_2 && (
            <p
              className={cn(
                isV2
                  ? // Figma: section title is exactly 28px on mobile, 36px on desktop.
                    "font-poppins font-semibold leading-[1.25] tracking-[-0.015em] text-[28px] sm:text-[36px]"
                  : "gradient-title-denton",
              )}
              style={{
                backgroundImage: `linear-gradient(${title_2_gradient_angle}, ${title_2_gradient_start_color}, ${title_2_gradient_end_color})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {title_2}
            </p>
          )}
        </div>

        {/* ── Mobile View ── */}
        <div className="xs:flex sm:hidden flex-col gap-[24px]">
          {/* Mobile Carousel */}
          <div className="overflow-hidden" ref={mobileEmblaRef}>
            <div className="flex gap-[16px]">
              {activeCards.map((item) => (
                <div key={item.id} className="flex-none w-full">
                  <MobileFacultyCard item={item} />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation */}
          {activeCards.length > 1 && (
            <div className="flex items-center gap-[20px] w-full">
              <button
                data-cta-name="carousel_nav"
                onClick={scrollMobilePrev}
                className={cn(
                  "flex items-center justify-center rounded-[80px] border cursor-pointer p-[10px] shrink-0",
                  isV2 ? "border-black text-black" : "border-white text-white",
                )}
                aria-label="Previous slide"
              >
                {left_navigation_icon ? (
                  <Image
                    src={left_navigation_icon}
                    alt={left_navigation_icon_alt}
                    width={24}
                    height={24}
                    className="size-[24px]"
                  />
                ) : isV2 ? (
                  <ArrowLeftLineIcon className="size-[24px]" />
                ) : (
                  <ArrowLeftIcon />
                )}
              </button>

              <div
                className="relative flex-1 rounded-[10px] overflow-hidden h-[6px]"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-[10px] transition-all duration-300 ease-out"
                  style={{
                    width: `${mobileProgressWidth}%`,
                    backgroundColor: range_color,
                  }}
                />
              </div>

              <button
                data-cta-name="carousel_nav"
                onClick={scrollMobileNext}
                className={cn(
                  "flex items-center justify-center rounded-[80px] border cursor-pointer p-[10px] shrink-0",
                  isV2 ? "border-black text-black" : "border-white text-white",
                )}
                aria-label="Next slide"
              >
                {right_navigation_icon ? (
                  <Image
                    src={right_navigation_icon}
                    alt={right_navigation_icon_alt}
                    width={24}
                    height={24}
                    className="size-[24px]"
                  />
                ) : isV2 ? (
                  <ArrowRightLineIcon className="size-[24px]" />
                ) : (
                  <ArrowRightIcon />
                )}
              </button>
            </div>
          )}

          {/* Mobile CTA */}
          <button
            data-cta-name="apply_now"
            className={cn(
              "flex items-center justify-center gap-[8px] cursor-pointer transition-opacity hover:opacity-90 w-full px-[24px] py-[16px]",
              isV2 && "rounded-[8px]",
            )}
            style={{
              backgroundColor: cta_button_color,
              color: cta_text_color,
              ...(isV2 && {
                filter: `drop-shadow(0px 10px 10px ${shadowFromColor(cta_button_color)})`,
              }),
            }}
            onClick={() => {
              ctaClick({
                section: "faculty_section",
                component: "apply_button",
                label: "apply",
                step: "1",
                entity: "faculty_section",
                parentEntity: "faculty_section",
              });
              dispatch(
                handleOpenLeaddrop({
                  variant: LEAD_DROP_VARIANTS.REGISTER,
                }),
              );
            }}
          >
            <span
              className={cn(
                "leading-[1.4] text-[16px]",
                isV2
                  ? "font-poppins font-semibold"
                  : "font-semibold tracking-[0.16px]",
              )}
            >
              {cta_text || ""}
            </span>
            {cta_icon ? (
              <Image
                src={cta_icon}
                alt={cta_icon_alt}
                width={24}
                height={24}
                className="size-[24px]"
              />
            ) : isV2 ? (
              <ExportIcon className="size-[24px]" />
            ) : null}
          </button>
        </div>

        {/* ── Desktop View ── */}
        <div className="xs:hidden sm:block">
          <div
            className={cn(
              "sm:h-[40px]",
              "md:h-[46px]",
              "lg:h-[46px]",
              "xl:h-[46px]",
            )}
          />

          {/* Desktop Carousel */}
          <div
            className={cn(
              "overflow-hidden",
              "sm:pl-[60px]",
              "md:pl-[60px]",
              "lg:pl-[80px]",
              "xl:pl-[140px]",
            )}
            ref={emblaRef}
          >
            <div
              className={cn(
                "flex",
                "sm:gap-[20px]",
                "md:gap-[20px]",
                "lg:gap-[20px]",
                "xl:gap-[20px]",
              )}
            >
              {activeCards.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    "flex-none",
                    "sm:w-[493px]",
                    "md:w-[554px]",
                    "lg:w-[620px]",
                    "xl:w-[620px]",
                  )}
                >
                  <FacultyCard item={item} isV2={isV2} />
                </div>
              ))}
            </div>
          </div>

          {activeCards.length > 1 && (
            <>
              <div
                className={cn(
                  "sm:h-[40px]",
                  "md:h-[40px]",
                  "lg:h-[24px]",
                  "xl:h-[40px]",
                )}
              />

              {/* Desktop Navigation */}
              <div
                className={cn(
                  "flex items-center justify-center",
                  "sm:px-[60px]",
                  "md:px-[60px]",
                  "lg:px-[80px]",
                  "xl:px-[140px]",
                )}
              >
                <div
                  className={cn(
                    "flex items-center",
                    "sm:gap-[20px]",
                    "md:gap-[20px]",
                    "lg:gap-[20px]",
                    "xl:gap-[20px]",
                  )}
                >
                  <button
                    data-cta-name="carousel_nav"
                    onClick={scrollPrev}
                    className={cn(
                      "flex items-center justify-center rounded-full border cursor-pointer transition-opacity shrink-0",
                      isV2
                        ? "border-black bg-transparent text-black hover:bg-black/5"
                        : "border-white bg-white/20 text-white hover:bg-white/30",
                      "sm:p-[10px]",
                      "md:p-[10px]",
                      "lg:p-[10px]",
                      "xl:p-[10px]",
                    )}
                    aria-label="Previous slide"
                  >
                    {left_navigation_icon ? (
                      <Image
                        src={left_navigation_icon}
                        alt={left_navigation_icon_alt}
                        width={24}
                        height={24}
                        className={cn(
                          "sm:size-[24px]",
                          "md:size-[24px]",
                          "lg:size-[24px]",
                          "xl:size-[24px]",
                        )}
                      />
                    ) : isV2 ? (
                      <ArrowLeftLineIcon className="size-[24px]" />
                    ) : (
                      <ArrowLeftIcon />
                    )}
                  </button>

                  <div
                    className={cn(
                      "relative rounded-[10px] overflow-hidden",
                      "sm:w-[290px] sm:h-[6px]",
                      "md:w-[290px] md:h-[6px]",
                      "lg:w-[290px] lg:h-[6px]",
                      "xl:w-[290px] xl:h-[6px]",
                    )}
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
                  >
                    <div
                      className="absolute left-0 top-0 h-full rounded-[10px] transition-all duration-300 ease-out"
                      style={{
                        width: `${progressWidth}%`,
                        backgroundColor: range_color,
                      }}
                    />
                  </div>

                  <button
                    data-cta-name="carousel_nav"
                    onClick={scrollNext}
                    className={cn(
                      "flex items-center justify-center rounded-full border cursor-pointer transition-opacity shrink-0",
                      isV2
                        ? "border-black bg-transparent text-black hover:bg-black/5"
                        : "border-white bg-white/20 text-white hover:bg-white/30",
                      "sm:p-[10px]",
                      "md:p-[10px]",
                      "lg:p-[10px]",
                      "xl:p-[10px]",
                    )}
                    aria-label="Next slide"
                  >
                    {right_navigation_icon ? (
                      <Image
                        src={right_navigation_icon}
                        alt={right_navigation_icon_alt}
                        width={24}
                        height={24}
                        className={cn(
                          "sm:size-[24px]",
                          "md:size-[24px]",
                          "lg:size-[24px]",
                          "xl:size-[24px]",
                        )}
                      />
                    ) : isV2 ? (
                      <ArrowRightLineIcon className="size-[24px]" />
                    ) : (
                      <ArrowRightIcon />
                    )}
                  </button>
                </div>
              </div>

              <div
                className={cn(
                  "sm:h-[40px]",
                  "md:h-[40px]",
                  "lg:h-[40px]",
                  "xl:h-[40px]",
                )}
              />
            </>
          )}

          {/* With a single card the nav (and its trailing spacer) is hidden, so add
              the gap above the CTA here to keep its top spacing consistent. */}
          {activeCards.length <= 1 && (
            <div
              className={cn(
                "sm:h-[40px]",
                "md:h-[40px]",
                "lg:h-[40px]",
                "xl:h-[40px]",
              )}
            />
          )}

          {/* Desktop CTA */}
          <div
            className={cn(
              "flex items-center justify-center",
              "sm:px-[60px]",
              "md:px-[60px]",
              "lg:px-[80px]",
              "xl:px-[140px]",
            )}
          >
            <button
              data-cta-name="apply_now"
              className={cn(
                "flex items-center justify-center cursor-pointer transition-opacity hover:opacity-90",
                "sm:gap-[7.5px] sm:px-[18px] sm:py-[12px]",
                "md:gap-[8px] md:px-[24px] md:py-[16px]",
                "lg:gap-[8px] lg:px-[24px] lg:py-[16px]",
                "xl:gap-[8px] xl:px-[24px] xl:py-[16px]",
                isV2 && "rounded-[8px]",
              )}
              onClick={() => {
                ctaClick({
                  section: "faculty_section",
                  component: "apply_button",
                  label: "apply",
                  step: "1",
                  entity: "faculty_section",
                  parentEntity: "faculty_section",
                });
                dispatch(
                  handleOpenLeaddrop({
                    variant: LEAD_DROP_VARIANTS.REGISTER,
                  }),
                );
              }}
              style={{
                backgroundColor: cta_button_color,
                color: cta_text_color,
                ...(isV2 && {
                  filter: `drop-shadow(0px 10px 10px ${shadowFromColor(cta_button_color)})`,
                }),
              }}
            >
              <span
                className={cn(
                  "leading-140",
                  isV2
                    ? "font-poppins font-semibold text-[16px]"
                    : cn(
                        "font-semibold",
                        "sm:text-[12px] sm:tracking-[0.12px]",
                        "md:text-[16px] md:tracking-[0.16px]",
                        "lg:text-[16px] lg:tracking-[0.16px]",
                        "xl:text-[16px] xl:tracking-[0.16px]",
                      ),
                )}
              >
                {cta_text}
              </span>
              {cta_icon ? (
                <Image
                  src={cta_icon}
                  alt={cta_icon_alt}
                  width={24}
                  height={24}
                  className={
                    isV2
                      ? "size-[24px]"
                      : cn(
                          "sm:size-[14px]",
                          "md:size-[18px]",
                          "lg:size-[18px]",
                          "xl:size-[18px]",
                        )
                  }
                />
              ) : isV2 ? (
                <ExportIcon className="size-[24px]" />
              ) : null}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faculty2;
